import { request as requester } from 'sonata-react-components';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';
import { BaseProcessUpload } from 'sonata-static-storage/lib/schemas/processUpload/processUpload.schema';
import { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';

import type { MultiFlightEntityType, PaginationResult, Shape } from 'sonata-static-storage/lib/common/common.types';
import type { MapBoxStreets } from './multiFlightMap.types';

export async function getLocations(
  country: string,
  query: string,
  locationIds: string[]
): Promise<PaginationResult<Shape>> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}cartography/shapes`, {
    data: {
      ...(locationIds.length > 0
        ? {
            filters: {
              excludedIds: locationIds
            }
          }
        : {}),
      fields: ['id', 'name', 'description', 'shape', 'boundingBox'],
      recoverUpperShape: false,
      stringFilter: query
    },
    method: 'POST',
    params: {
      country,
      itemsPerPage: 5,
      limit: 5,
      page: 0,
      skip: 0
    }
  });
}

export async function getStreets(query: string, country: string): Promise<MapBoxStreets> {
  return requester.makeRequest(
    `${taptap.config.mapbox.url}${taptap.config.mapbox.geocoderURL}${encodeURIComponent(query)}.json`,
    {
      method: 'GET',
      params: {
        access_token: taptap.config.mapbox.accessToken,
        country,
        limit: 5,
        types: 'address'
      }
    }
  );
}

export async function getScreensInfo(
  country: string,
  ids: string[],
  excludeIds: string[] = [],
  multipleIdFields: string[] = []
): Promise<{
  items: GeoDataAndScreensInteface[];
  total: number;
  page: number;
  pageSize: number;
}> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}sources/screens`, {
    data: {
      excludeIds,
      ids,
      multipleIdFields
    },
    method: 'POST',
    params: {
      country: country.toUpperCase(),
      itemsPerPage: -1
    }
  });
}

export async function getSourcesMetadata(
  countryCode?: string,
  sourceId?: string,
  userId?: string,
  campaignId?: string,
  optionsByCountry?: boolean,
  type?: string
): Promise<any[]> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}sources`, {
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'GET',
    params: {
      ...(countryCode && { country: normalizeCountryToCca3(countryCode).toUpperCase() }),
      ...(sourceId && { sourceId }),
      ...(userId && { userId }),
      ...(campaignId && { campaignId }),
      ...(optionsByCountry && { optionsByCountry }),
      ...(type && { type })
    }
  });
}

export function getProcessResults(processId: string, token: Record<string, any> = {}): Promise<any> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}upload/process/${processId}/results`,
    {
      method: 'GET',
      params: {
        unlimited: true
      }
    },
    token
  );
}

export function processEntityFile(
  entity: MultiFlightEntityType,
  entityId: string,
  fileType: 'zones' | 'venues',
  fileData: {
    columnDelimiter?: string;
    country?: string;
    decimalDelimiter?: string;
    fileName: string;
    keysToReview: { fileCol: string; key: string }[];
    lineDelimiter?: string;
    totalLines?: number;
  },
  uploadProcess: BaseProcessUpload['uploadProcess'],
  prevSonataUploadProcessProcessId: string | null,
  poiFinderEnabled?: boolean,
  token?: Record<string, string>
): Promise<{ processId?: string }> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}upload/${fileType}/file/process/${entity}/${entityId}`, {
    data: JSON.stringify({
      ...(fileData.country
        ? {
            country: fileData.country
          }
        : {}),
      delimiter: {
        columnDelimiter: fileData.columnDelimiter || '',
        decimalDelimiter: fileData.decimalDelimiter || '.',
        lineDelimiter: fileData.lineDelimiter || ''
      },
      fileName: fileData.fileName,
      keysToReview: fileData.keysToReview,
      totalLines: fileData.totalLines?.toString() || '0',
      uploadProcess,
      ...(prevSonataUploadProcessProcessId && { prevSonataUploadProcessProcessId }),
      ...(poiFinderEnabled && { poiFinderEnabled })
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST',
    params: {
      socketEvent: true
    },
    token
  });
}

export type ProcessUpdateStatus = 'discard' | 'cancelled' | 'accept';

export async function updateProcessStatus(
  processId: string,
  newStatus: ProcessUpdateStatus,
  token: Record<string, any> = {}
): Promise<void> {
  await requester.makeRequest(`${taptap.config.backendUrlBase}upload/process/${processId}/status`, {
    data: {
      status: newStatus
    },
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'PUT',
    token
  });
}

export function uploadEntityFile(
  entity: MultiFlightEntityType,
  entityId: string,
  fileType: 'zones' | 'venues',
  file: File,
  token: Record<string, string>
): Promise<any> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}upload/${fileType}/file/${entity}/${entityId}`,
    {
      data: file,
      headers: {
        'Content-Type': file?.type
      },
      method: 'POST',
      params: {
        aspectRatio: undefined,
        name: encodeURIComponent(file?.name || '')
      }
    },
    token
  );
}

export async function getAttributionPOIsInfo(
  sourceId: string,
  country: string,
  ids: string[],
  excludeIds: string[] = [],
  multipleIdFields: string[] = []
): Promise<{
  items: GeoDataAndScreensInteface[];
  total: number;
  page: number;
  pageSize: number;
}> {
  const url = `${taptap.config.backendUrlBase}sources/${sourceId}`;

  return requester.makeRequest(url, {
    data: {
      excludeIds,
      ids,
      multipleIdFields
    },
    method: 'POST',
    params: {
      country: country.toUpperCase(),
      itemsPerPage: -1
    }
  });
}

export function getProcessErrorsCSVUrl(processId: string): Promise<{ url: string }> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}upload/process/${processId}/errors`, {
    method: 'GET'
  });
}
